import { useEffect, useRef, useState } from "react";
import useHijos from "../../hooks/useHijos";
import Swal from "sweetalert2";

const IVA = 0.16;

/**
 * Alta de una partida en el mismo renglón de la tabla.
 *
 * Hay tres formas: la cuenta padre sólo lleva número y nombre, la subcuenta
 * igual (sólo agrupa), y el concepto lleva proveedor, variable, montos y
 * calendario de pagos -- son los mismos campos que pedía el modal viejo
 * "Agregar Hijos", incluyendo la opción de cargar varios conceptos antes de
 * guardar (el botón "+").
 */
const CLASIFICACIONES = [
  "Arriba de la linea",
  "Produccion",
  "Post produccion",
  "Otros",
];

const OPCIONES_PAGO = [
  { value: "unico", label: "Unico" },
  { value: "semanal", label: "Semanal" },
  { value: "quincenal", label: "Quincenal" },
  { value: "mensual", label: "Mensual" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

/** Siguiente cuenta cuando se agrega otro concepto con el botón "+". */
const incrementarCuenta = (cuenta) => {
  const partes = String(cuenta || "").split(".");
  const ultimo = parseInt(partes[partes.length - 1], 10);
  if (Number.isNaN(ultimo)) return cuenta;
  partes[partes.length - 1] = ultimo + 1;
  return partes.join(".");
};

const filaVacia = (cuenta) => ({
  nombre: "",
  cuenta: cuenta ?? "",
  proveedor: "",
  variable: "",
  mostrarVariable: false,
  nombreVariable: "",
  valorVariable: "",
  cantidad: "",
  unidad: "",
  veces: "",
  costo: "",
  conIva: true,
  fecha: "",
  opcion: "",
  cuantos: "",
});

const calcularMontos = (fila) => {
  const subtotal =
    (parseFloat(fila.cantidad) || 0) *
    (parseFloat(fila.veces) || 1) *
    (parseFloat(fila.costo) || 0);
  const iva = fila.conIva ? subtotal * IVA : 0;

  return { subtotal, iva, total: subtotal + iva };
};

export default function FilaNueva({
  tipo,
  cuentaSugerida,
  sugerirCuenta,
  token,
  clienteId,
  onGuardar,
  onCancelar,
}) {
  const [grupo, setGrupo] = useState(CLASIFICACIONES[0]);
  const esCuenta = tipo === "cuenta";
  const esConcepto = tipo === "concepto";

  // Para cuentas padre el número se propone según la clasificación (bloques
  // de 100: 1100, 1200... para "Arriba de la linea", 2000... para
  // "Produccion", etc). Se recalcula si el usuario cambia la clasificación,
  // pero sólo mientras no haya escrito su propio número.
  const sugerenciaInicial = esCuenta && sugerirCuenta ? sugerirCuenta(grupo) : cuentaSugerida ?? "";
  const [cuenta, setCuenta] = useState(sugerenciaInicial);
  const ultimaSugerencia = useRef(sugerenciaInicial);

  useEffect(() => {
    if (!esCuenta || !sugerirCuenta) return;
    const nueva = sugerirCuenta(grupo);
    setCuenta((actual) => (actual === ultimaSugerencia.current ? nueva : actual));
    ultimaSugerencia.current = nueva;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grupo]);

  const [nombre, setNombre] = useState("");
  const [guardando, setGuardando] = useState(false);

  // --- Conceptos: proveedor, variable y calendario de pagos, con opción de
  // cargar varios antes de guardar (lo que en el modal viejo era el botón "+").
  const {
    error: errorVariable,
    message: mensajeVariable,
    success: exitoVariable,
    Variables,
    Proveedores,
    VariableGetService,
    ProveedorGetService,
    VariableAddService,
  } = useHijos();
  const [filas, setFilas] = useState(() => [filaVacia(cuentaSugerida)]);
  const filaVariableEnCurso = useRef(null);

  useEffect(() => {
    if (!esConcepto) return;
    VariableGetService(token);
    ProveedorGetService(clienteId, token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esConcepto]);

  useEffect(() => {
    if (!esConcepto || !exitoVariable) return;
    Swal.close();
    Swal.fire({icon: "success", title: "Exito", confirmButtonColor: "#3b82f6", text: mensajeVariable});
    VariableGetService(token);
    if (filaVariableEnCurso.current !== null) {
      const i = filaVariableEnCurso.current;
      setFilas((actuales) =>
        actuales.map((f, idx) =>
          idx === i ? {...f, mostrarVariable: false, nombreVariable: "", valorVariable: ""} : f
        )
      );
      filaVariableEnCurso.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exitoVariable]);

  useEffect(() => {
    if (!esConcepto || !errorVariable) return;
    Swal.close();
    Swal.fire({icon: "error", title: "Error", confirmButtonColor: "#3b82f6", text: mensajeVariable});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorVariable]);

  const actualizarFila = (indice, cambios) => {
    setFilas((actuales) => actuales.map((f, i) => (i === indice ? {...f, ...cambios} : f)));
  };

  const agregarFila = () => {
    const ultima = filas[filas.length - 1];
    setFilas([...filas, filaVacia(incrementarCuenta(ultima?.cuenta))]);
  };

  const quitarFila = (indice) => {
    setFilas((actuales) => actuales.filter((_, i) => i !== indice));
  };

  const seleccionarVariable = (indice, opcion) => {
    actualizarFila(indice, {
      variable: opcion || "",
      costo: opcion?.precio ?? filas[indice].costo,
    });
  };

  const agregarVariable = (indice) => {
    const fila = filas[indice];
    if (!fila.nombreVariable.trim()) return;

    const data = new FormData();
    data.append("nombre", fila.nombreVariable);
    data.append("cantidad", fila.valorVariable);

    filaVariableEnCurso.current = indice;
    VariableAddService(data, token);
    Swal.fire({
      title: "Registrando variable",
      text: "Loading...",
      allowEscapeKey: false,
      allowOutsideClick: false,
      showConfirmButton: false,
      icon: "question",
      onOpen: () => Swal.showLoading(),
    });
  };

  const enviar = async (evento) => {
    evento.preventDefault();

    setGuardando(true);
    try {
      if (esConcepto) {
        if (filas.some((f) => !f.nombre.trim() || !f.cuenta.trim())) return;

        const items = filas.map((f) => {
          const m = calcularMontos(f);
          return {
            NombreH: f.nombre.trim(),
            CuentaPre: f.cuenta.trim(),
            VariableID: f.variable || "",
            ProveedorID: f.proveedor || "",
            Cantidad: f.cantidad,
            X: f.veces,
            Unidades: f.unidad,
            IsChecked: f.conIva,
            Fecha: f.fecha,
            Opcion: f.opcion || "",
            Cuantos: f.cuantos,
            Costo: f.costo,
            Subtota: m.subtotal,
            Iva: m.iva,
            Total: m.total,
          };
        });

        await onGuardar(items);
        return;
      }

      if (!nombre.trim() || !cuenta.trim()) return;

      await onGuardar({
        cuenta: cuenta.trim(),
        nombre: nombre.trim(),
        grupo,
      });
    } finally {
      setGuardando(false);
    }
  };

  if (esConcepto) {
    return (
      <form className="gtable-form gtable-form-conceptos" onSubmit={enviar}>
        {filas.map((fila, i) => {
          const m = calcularMontos(fila);
          return (
            <div className="gtable-form-fila" key={i}>
              <div className="gtable-form" style={{marginBottom: "0.5rem"}}>
                <div className="gtable-form-campo es-ancho">
                  <label htmlFor={`fila-proveedor-${i}`}>Proveedor</label>
                  <select
                    id={`fila-proveedor-${i}`}
                    value={fila.proveedor?.value ?? ""}
                    onChange={(e) => {
                      const opt = Proveedores.find((p) => String(p.value) === e.target.value);
                      actualizarFila(i, {proveedor: opt ?? ""});
                    }}
                  >
                    <option value="">Sin proveedor</option>
                    {Proveedores.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-cuenta-${i}`}>Cuenta</label>
                  <input
                    id={`fila-cuenta-${i}`}
                    value={fila.cuenta}
                    onChange={(e) => actualizarFila(i, {cuenta: e.target.value})}
                    placeholder="Ej. 1102"
                    style={{width: "6rem"}}
                    required
                  />
                </div>

                <div className="gtable-form-campo es-ancho">
                  <label htmlFor={`fila-concepto-${i}`}>Concepto</label>
                  <input
                    id={`fila-concepto-${i}`}
                    value={fila.nombre}
                    onChange={(e) => actualizarFila(i, {nombre: e.target.value})}
                    placeholder="Renta de equipo"
                    autoFocus={i === 0}
                    required
                  />
                </div>

                <div className="gtable-form-campo es-ancho">
                  <label htmlFor={`fila-variable-${i}`}>Nombre de la variable</label>
                  <select
                    id={`fila-variable-${i}`}
                    value={fila.variable?.value ?? ""}
                    onChange={(e) => {
                      const opt = Variables.find((v) => String(v.value) === e.target.value);
                      seleccionarVariable(i, opt ?? "");
                    }}
                  >
                    <option value="">Sin variable</option>
                    {Variables.map((v) => (
                      <option key={v.value} value={v.value}>{v.label}</option>
                    ))}
                  </select>
                </div>

                <button
                  type="button"
                  className="crud-secondary-btn"
                  onClick={() => actualizarFila(i, {mostrarVariable: !fila.mostrarVariable})}
                >
                  <i className="bi bi-plus-circle" aria-hidden="true"></i> Agregar variable
                </button>

                <label className="gtable-form-iva">
                  <input
                    type="checkbox"
                    checked={fila.conIva}
                    onChange={(e) => actualizarFila(i, {conIva: e.target.checked})}
                  />
                  IVA
                </label>
              </div>

              {fila.mostrarVariable && (
                <div className="gtable-form" style={{marginBottom: "0.5rem"}}>
                  <div className="gtable-form-campo">
                    <label>Nombre variable</label>
                    <input
                      value={fila.nombreVariable}
                      onChange={(e) => actualizarFila(i, {nombreVariable: e.target.value})}
                      placeholder="Nombre variable"
                    />
                  </div>
                  <div className="gtable-form-campo">
                    <label>Valor variable</label>
                    <input
                      value={fila.valorVariable}
                      onChange={(e) => actualizarFila(i, {valorVariable: e.target.value})}
                      placeholder="Valor variable"
                    />
                  </div>
                  <button type="button" className="crud-add-btn" onClick={() => agregarVariable(i)}>
                    Agregar
                  </button>
                </div>
              )}

              <div className="gtable-form" style={{marginBottom: "0.5rem"}}>
                <div className="gtable-form-campo">
                  <label htmlFor={`fila-cantidad-${i}`}>Cantidad</label>
                  <input
                    id={`fila-cantidad-${i}`}
                    type="number"
                    step="any"
                    value={fila.cantidad}
                    onChange={(e) => actualizarFila(i, {cantidad: e.target.value})}
                    style={{width: "6rem"}}
                  />
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-unidad-${i}`}>Unidad</label>
                  <input
                    id={`fila-unidad-${i}`}
                    value={fila.unidad}
                    onChange={(e) => actualizarFila(i, {unidad: e.target.value})}
                    placeholder="pieza"
                    style={{width: "7rem"}}
                  />
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-veces-${i}`}>X</label>
                  <input
                    id={`fila-veces-${i}`}
                    type="number"
                    step="any"
                    value={fila.veces}
                    onChange={(e) => actualizarFila(i, {veces: e.target.value})}
                    style={{width: "5rem"}}
                  />
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-costo-${i}`}>Costo</label>
                  <input
                    id={`fila-costo-${i}`}
                    type="number"
                    step="any"
                    value={fila.costo}
                    disabled={!!fila.variable}
                    onChange={(e) => actualizarFila(i, {costo: e.target.value})}
                    style={{width: "7rem"}}
                  />
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-fecha-${i}`}>Fecha primer pago</label>
                  <input
                    id={`fila-fecha-${i}`}
                    type="date"
                    value={fila.fecha}
                    onChange={(e) => actualizarFila(i, {fecha: e.target.value})}
                  />
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-opcion-${i}`}>Opcion de pagos</label>
                  <select
                    id={`fila-opcion-${i}`}
                    value={fila.opcion?.value ?? ""}
                    onChange={(e) => {
                      const opt = OPCIONES_PAGO.find((o) => o.value === e.target.value);
                      actualizarFila(i, {opcion: opt ?? ""});
                    }}
                  >
                    <option value="">Sin opción</option>
                    {OPCIONES_PAGO.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="gtable-form-campo">
                  <label htmlFor={`fila-cuantos-${i}`}>Cuantos pagos</label>
                  <input
                    id={`fila-cuantos-${i}`}
                    type="number"
                    value={fila.cuantos}
                    onChange={(e) => actualizarFila(i, {cuantos: e.target.value})}
                    style={{width: "6rem"}}
                  />
                </div>

                <span className="gtable-form-total">
                  {m.total.toLocaleString("en-US", {style: "currency", currency: "USD"})}
                </span>

                {filas.length > 1 && (
                  <button
                    type="button"
                    className="crud-icon-btn delete"
                    title="Quitar este concepto"
                    onClick={() => quitarFila(i)}
                  >
                    <i className="bi bi-trash" aria-hidden="true"></i>
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div className="gtable-form-acciones">
          <button type="button" className="crud-secondary-btn" onClick={agregarFila}>
            <i className="bi bi-plus-lg" aria-hidden="true"></i> Agregar otro concepto
          </button>
          <button type="submit" className="crud-add-btn" disabled={guardando}>
            {guardando ? <span className="spinner-boton" aria-hidden="true" /> : <i className="bi bi-check-lg" aria-hidden="true"></i>}
            Guardar
          </button>
          <button type="button" className="crud-secondary-btn" onClick={onCancelar} disabled={guardando}>
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  return (
    <form className="gtable-form" onSubmit={enviar}>
      <div className="gtable-form-campo">
        <label htmlFor="fila-cuenta">Cuenta</label>
        <input
          id="fila-cuenta"
          value={cuenta}
          onChange={(e) => setCuenta(e.target.value)}
          placeholder="Ej. 1102"
          style={{ width: "6rem" }}
          required
        />
      </div>

      <div className="gtable-form-campo es-ancho">
        <label htmlFor="fila-nombre">
          {esCuenta ? "Nombre de la cuenta" : "Nombre de la subcuenta"}
        </label>
        <input
          id="fila-nombre"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={esCuenta ? "FOTOGRAFÍA" : "Personal de producción"}
          autoFocus
          required
        />
      </div>

      {esCuenta && (
        <div className="gtable-form-campo">
          <label htmlFor="fila-grupo">Clasificación</label>
          <select
            id="fila-grupo"
            value={grupo}
            onChange={(e) => setGrupo(e.target.value)}
          >
            {CLASIFICACIONES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="gtable-form-acciones">
        <button type="submit" className="crud-add-btn" disabled={guardando}>
          {guardando ? (
            <span className="spinner-boton" aria-hidden="true" />
          ) : (
            <i className="bi bi-check-lg" aria-hidden="true"></i>
          )}
          Guardar
        </button>
        <button
          type="button"
          className="crud-secondary-btn"
          onClick={onCancelar}
          disabled={guardando}
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
